/**
 * Pay-run read path + history (C3, #22327) — replaces the interim
 * dev-bypass-only `dev_pay_runs.ts` (deleted). Self-fetching hook, NOT a
 * `dev_*.ts`/`useDevBackendData` boot-time seed — see `use_session_facts.ts`'s
 * module docblock for the same "why": this is real production OAuth-gated
 * read work, live in every environment (including production), not just
 * local dev.
 *
 * Uses the real bearer client (`getTrpcClient()`, `lib/trpc.ts`) and the
 * shared `useOAuthEntityContext()` helper (`oauth_entity_context.ts`) —
 * never `firm.list`/`firm.create`/`entity.list`. Mirrors `dev_pay_runs.ts`'s
 * (deleted) resolution chain exactly: `payroll.listPayRuns` for the entity's
 * pay-run summary rows, then `payroll.getPayRun` per row for its lines, then,
 * for `draft` rows only, `payroll.getPayRunJournalPreview` for its dry-run
 * statutory breakdown — see `wagepro_store_pay_runs.ts`'s module docblock for
 * why a UI `PayRun` needs all three calls, not just `listPayRuns`.
 *
 * ── No mock fallback (the single highest-risk mock leak in this epic) ─────
 *
 * A FORBIDDEN from `payroll.listPayRuns`/`getPayRun`/`getPayRunJournalPreview`
 * — SPECIFICALLY the one meaning the entity is missing the `payroll` add-on
 * entitlement (#21675, not yet grantable to a real customer), classified by
 * `isPayrollAddonRequiredError` (`lib/trpc.ts`) — resolves an explicit
 * `entitlement_required` state, NEVER `MOCK_PAY_RUNS`' fake `run-1`-style
 * history. This is exactly what the OLD `dev_pay_runs.ts` silently degraded
 * to (see its own deleted docblock) and is the single highest-risk mock leak
 * called out in the whole epic (`docs/epics/wagepro-production-auth.md`).
 *
 * Every OTHER failure — including every OTHER reason `payroll.listPayRuns`
 * can throw FORBIDDEN (a missing OAuth scope, a null-audience token, no
 * entity access, or an inactive subscription — see
 * `isPayrollAddonRequiredError`'s own docblock in `lib/trpc.ts`) — resolves
 * the generic `error` state instead. Collapsing every FORBIDDEN into
 * `entitlement_required` would mask a real auth bug (e.g. a scope-grant
 * regression, C6 #21929) behind a misleading "buy the add-on" prompt
 * (adversarial review, PR #22370, finding 2).
 *
 * `derivePayRunsFromRecords`/`RealPayRunRecord`
 * (`wagepro_store_pay_runs.ts`) are the permanent, UNCHANGED adapter this
 * hook calls once records resolve — only HOW records get resolved changes.
 *
 * ── What stays out of scope (C7/C8/C9/C10) ─────────────────────────────────
 *
 * `acknowledge_exception`/`post_actions` are still sourced from
 * `useWageProStore()`, UNCHANGED — client-local exception acknowledgement
 * (never persisted, per its own established design) and post-run actions
 * (C8/C9) are explicitly out of scope for this pass.
 *
 * ── Write path (C7, #22329): `start_pay_run` / `sign_off` ──────────────────
 *
 * Real `payroll.createPayRun` / `payroll.approvePayRun` (both allowlisted
 * `payroll:employer:write`-scope mutations). Both are `undefined`/no-op
 * unless ALL of the following hold, mirroring `main.tsx`'s old
 * `hasRealPayRuns` invariant but stronger (that guard only checked "entity
 * context resolved"; this checks "the real read actually succeeded"):
 *
 *   - `state.status === 'ready'` — never wire a write controller over an
 *     absent or errored pay-run read (a real `approvePayRun` fired at a
 *     stale/mock id would silently no-op or 404 — the exact failure mode
 *     the old `hasRealPayRuns` guard existed to prevent).
 *   - `grant.mode !== 'read'` — a read-only OAuth token hides the write
 *     affordances entirely rather than offering buttons that would 403.
 *   - `grant.scopes` actually contains `payroll:employer:write` (second-round
 *     adversarial review, PR #22375 finding 3) — `grant.mode` and
 *     `grant.scopes` are two independently-sourced fields on `OAuthGrant`
 *     (`auth_context.tsx`), and nothing enforces they agree: a grant could
 *     resolve `mode: 'write'` while the underlying token only actually
 *     carries `payroll:employer:read` (a stale/inconsistent grant record, or
 *     a future `auth.oauthGrant` bug). Checking `mode` alone would render the
 *     write buttons anyway and let the mutation itself 403 — exactly the
 *     failure mode the acceptance criterion ("a read-only OAuth token hides
 *     the write affordances entirely rather than offering buttons that would
 *     403") exists to prevent. Both checks must hold.
 *
 * Failures NEVER flip local UI state to a fabricated success — both
 * mutations are pessimistic (mirrors `wagepro_store.tsx`'s
 * `update_organisation`, the only other real write mutation in this
 * codebase: state only changes after the mutation resolves `ok`, an error
 * leaves everything untouched and is returned to the caller to render).
 * Callers (`run_payroll_screen.tsx`/`wizard_steps.tsx`) own the "is this in
 * flight" / "did this just fail" local UI state, same as
 * `settings_screen.tsx`'s `handle_save`.
 *
 * ── AAL2 step-up on sign-off (#22821) ───────────────────────────────────────
 *
 * `approvePayRun` is a real `assertMoneyWriteAllowed`-gated mutation — a
 * session that was never established with two-factor authentication (AAL1)
 * is rejected with `UNAUTHORIZED` + a `ledgerCode` the FRONTEND never used to
 * surface, which meant an AAL1 user calling sign-off saw only the generic
 * `'error'` "Couldn't sign off this pay run. Please try again." — a blind
 * retry that can NEVER succeed, because an AAL1 token never self-upgrades to
 * AAL2; only a fresh sign-in round trip does. {@link PayRunMutationFailure}
 * adds the `'step_up_required'` classification (via {@link isStepUpRequired},
 * `lib/auth/step_up.ts`) so `wizard_steps.tsx`'s `ApprovalStep` can render
 * the real blocker and the two genuine ways forward (set up 2FA, then sign
 * in again) instead of a dead-end retry loop. `startPayRun`/`createPayRun`
 * gets the identical, defensive check even though it isn't documented as
 * money-write-gated today — same "degrade correctly if the gate widens"
 * stance `isPayrollAddonRequiredError`'s own defensive check already takes
 * on this function. `run_payroll_screen.tsx`'s `start_error` rendering gets
 * the same three-way branch treatment as `ApprovalStep`'s `sign_off_error`
 * (a dedicated `pay_run.start_step_up_required` guidance message, distinct
 * copy from the sign-off one since "starting" and "signing off" a pay run
 * are different actions), so a future gate change on `createPayRun` degrades
 * to the correct UI, not a silent fallthrough to the generic "please try
 * again" this whole fix exists to eliminate.
 *
 * ── `createPayRun`'s GL accounts: reused from history, never guessed ───────
 *
 * `payroll.createPayRun` requires `wages_expense_account_id` /
 * `wages_payable_account_id` — real chart-of-accounts row ids. The OLD dev
 * bypass (`dev_pay_run_actions.ts`, now deleted) resolved these via
 * `account.list` with hardcoded GL codes 477/803. That path does not exist
 * for a real OAuth token: `account.list` is deliberately NOT allowlisted for
 * `payroll:employer:*` (C5, #22324's whole point — exposing the full COA
 * with balances to a payroll-scoped token is the wrong fix, explicitly
 * rejected). Nor can C5's own `payroll.listPaymentSourceAccounts` supply
 * them — verified by reading its SQL WHERE clause
 * (`apps/api/src/trpc/routers/payroll.ts`): it filters to
 * `account_type === 'asset'` with bank/cash sub-types ONLY, so a Wages
 * EXPENSE account and a Wages Payable LIABILITY account can never appear in
 * that result set. Widening either surface would either re-expose the COA
 * (C5's own rejected fix) or accept a structurally wrong account type into a
 * GL posting — both are money-rules violations, not options here.
 *
 * The correct, root-cause resolution: `payroll.listPayRuns` already returns
 * the FULL `pay_runs` row (unprojected `select()`,
 * `apps/api/src/trpc/routers/payroll.ts`), which includes THIS entity's own
 * `wages_expense_account_id`/`wages_payable_account_id` on every existing
 * pay run — an entity's wages accounts don't ordinarily change between
 * runs, so reusing the most recently created run's already-persisted ids is
 * not a guess. {@link resolveDefaultPayrollAccounts} does this from data
 * `fetchPayRunRecords` already fetches — zero new backend calls. `status` is
 * NOT part of the selection (see that function's own doc comment, second
 * round of adversarial review, PR #22375 finding 1, superseding the
 * `voided`-exclusion logic an earlier pass added) — a `voided` run's account
 * ids are exactly as (non-)stale as any other row's.
 *
 * IMPORTANT caveat (adversarial review finding 2): for the SL/generic path,
 * `wages_expense_account_id` is close to cosmetic — the actual posting
 * (`statutory_posting_service.ts`) re-resolves fixed GL codes 477/803
 * itself rather than trusting the stored id. For **Indonesia**, though,
 * this reused id IS load-bearing: it flows straight into
 * `buildIdStatutoryJournalLines` (`payroll.ts`, `approvePayRun`) and
 * `finalisePayRun` credits `wages_payable_account_id` directly for ANY
 * jurisdiction. Neither `createPayRun` nor `approvePayRun` validates the
 * account's type or archived status server-side (bare `z.string().min(1)`,
 * no `accounts` table lookup) as of this writing — that hardening is
 * tracked separately as #22725 (concurrent, independent work).
 *
 * ── First-ever pay run: backend fallback resolution (#22374) ──────────────
 *
 * An entity with zero prior pay runs has nothing for
 * {@link resolveDefaultPayrollAccounts} to reuse — it returns `null` in that
 * case. {@link fetchPayRunRecords} then falls back to the real
 * `payroll.getDefaultPayrollAccounts` procedure (`apps/api/src/trpc/routers/
 * payroll.ts`), a narrow, entity-scoped resolution of ONLY the two fixed-code
 * GL accounts (`477` Wages Expense, `803` Wages Payable) — never the full COA
 * (`account.list` is still deliberately not allowlisted for
 * `payroll:employer:*`) and never a guess. {@link mapFallbackDefaultAccounts}
 * converts its `{wages_expense, wages_payable}` response (each nullable —
 * the backend returns `null` for a role rather than throwing when the
 * entity's COA is missing or has mis-typed one of these codes) into the same
 * both-or-null shape {@link resolveDefaultPayrollAccounts} produces. A
 * failure of the fallback CALL ITSELF (network, an unexpected FORBIDDEN) is
 * caught inside {@link fetchPayRunRecords} and never escapes to poison the
 * rest of the (perfectly fine) pay-run history read — `defaultAccounts`
 * simply stays `null` and `start_pay_run` stays `undefined`, the same
 * "never offer a button that can't do anything" outcome as before #22374,
 * just reached one level deeper (history AND the narrow backend fallback
 * both came up empty) rather than reached immediately on zero history.
 *
 * History-derived resolution is tried FIRST and unconditionally short-circuits
 * the fallback: when any prior pay run carries both persisted ids, the new
 * `getDefaultPayrollAccounts` call is never made — zero new network requests
 * for the common case (an entity with pay-run history), byte-identical
 * behavior to pre-#22374.
 */
import * as React from 'react';
import { useWageProStore } from './wagepro_store';
import type { PayRunPostActionsController } from './wagepro_store';
import {
  getTrpcClient,
  isPayRunHasContractorError,
  isPayrollAddonRequiredError,
} from '../lib/trpc';
import { useAuth } from '../lib/auth/auth_context';
import type { OAuthGrant } from '../lib/auth/auth_context';
import { isStepUpRequired } from '../lib/auth/step_up.js';
import { useEmployeeRecords } from './use_employees.js';
import type { RealEmployeeRecord } from './wagepro_store_employees.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context';
import {
  derivePayRunsFromRecords,
  type RealPayRunRecord,
  type RealPayRunLine,
  type RealPayRunJournalPreview,
} from './wagepro_store_pay_runs';
import type { PayRun, PayrollReportId } from './types';

const OPEN_STATUSES = new Set<PayRun['status']>([
  'draft',
  'in_review',
  'approved',
]);

/** Not yet approved or voided (`draft`, or mock-only `in_review`). */
const IN_PROGRESS_STATUSES = new Set<PayRun['status']>(['draft', 'in_review']);

/**
 * A run still being prepared — nothing posted yet. The real backend never
 * moves a run past `approved` (finalising only sweeps expense claims), so
 * `approved` is effectively terminal: Run payroll offers starting the next
 * month once the latest run is no longer in progress.
 */
export function isInProgressPayRun(run: Pick<PayRun, 'status'>): boolean {
  return IN_PROGRESS_STATUSES.has(run.status);
}

/**
 * The run the Run payroll screen and dashboard focus on. `listPayRuns` has
 * no ORDER BY (rows come back in insertion order), so runs are ranked
 * newest-period-first here, then: the newest in-progress run, else the newest
 * approved run, else the newest run of any status, else `null`.
 */
export function select_current_run(pay_runs: readonly PayRun[]): PayRun | null {
  const newest_first = [...pay_runs].sort(
    (a, b) =>
      b.period.year - a.period.year ||
      b.period.month - a.period.month ||
      b.created_at.localeCompare(a.created_at),
  );
  return (
    newest_first.find(isInProgressPayRun) ??
    newest_first.find((run) => OPEN_STATUSES.has(run.status)) ??
    newest_first.at(0) ??
    null
  );
}

/**
 * The exact OAuth scope string a grant must carry for the write path to be
 * reachable — matches the backend allowlist `PAYROLL_EMPLOYER_WRITE`
 * (`apps/api/src/trpc/routers/payroll.ts`) that actually gates
 * `createPayRun`/`approvePayRun`, and `oauth_pkce.ts`'s `DEFAULT_SCOPE`
 * string this app requests at sign-in.
 */
const PAYROLL_EMPLOYER_WRITE_SCOPE = 'payroll:employer:write';

/**
 * Whether a grant is genuinely write-capable — BOTH `mode` and `scopes`
 * must agree, not `mode` alone (second-round adversarial review, PR
 * #22375 finding 3). `grant.mode` and `grant.scopes` are two
 * independently-sourced fields on `OAuthGrant` (`auth_context.tsx`) —
 * nothing enforces they agree, so a grant could resolve `mode: 'write'`
 * while the underlying token only actually carries
 * `payroll:employer:read` (a stale/inconsistent grant record, or a future
 * `auth.oauthGrant` bug). Trusting `mode` alone would render the write
 * buttons anyway and let the mutation itself 403 — exactly the failure
 * mode the acceptance criterion ("a read-only OAuth token hides the write
 * affordances entirely rather than offering buttons that would 403")
 * exists to prevent. A missing/`null` grant is never write-capable. Pure
 * and exported so it's unit-testable without rendering `usePayRuns()`
 * itself (this hook has no jsdom setup — see the pure-function-extraction
 * pattern this module already uses for
 * {@link resolveDefaultPayrollAccounts}/{@link resolvePayRunsReadState}).
 */
export function isWriteCapableGrant(
  grant: Pick<OAuthGrant, 'mode' | 'scopes'> | null | undefined,
): boolean {
  return (
    grant !== null &&
    grant !== undefined &&
    grant.mode !== 'read' &&
    grant.scopes.includes(PAYROLL_EMPLOYER_WRITE_SCOPE)
  );
}

/** How the real pay-run fetch can fail, classified by {@link isPayrollAddonRequiredError}. */
export type PayRunsLoadFailure = 'entitlement_required' | 'error';

/** Input the real fetch needs to identify the entity — same shape every `payroll.*` query takes. */
export interface PayRunsFetchInput {
  firm_id: string;
  entity_id: string;
}

/**
 * The `listPayRuns` summary row fields this hook actually reads — the real
 * wire row carries more (raw D1 columns), which structurally satisfies this
 * narrower shape. `wages_expense_account_id`/`wages_payable_account_id` are
 * read here (never surfaced to the UI's `PayRun` type) purely so
 * {@link resolveDefaultPayrollAccounts} can resolve `createPayRun`'s
 * required GL accounts from history — see module docblock.
 */
export interface PayRunSummaryRow {
  id: string;
  period_start: string;
  period_end: string;
  status: string;
  total_gross_cents: number;
  created_at: string;
  /** `pay_runs.approved_by` (user id). Optional only so narrow test fixtures need not spell it; the real wire row always carries it. */
  approved_by?: string | null;
  /** `pay_runs.approved_at` (ISO timestamp). Optional for the same reason as `approved_by`. */
  approved_at?: string | null;
  wages_expense_account_id: string | null;
  wages_payable_account_id: string | null;
}

/**
 * One resolved GL account role from `payroll.getDefaultPayrollAccounts`
 * (#22374) — mirrors that procedure's `.output()` shape exactly.
 */
export interface DefaultPayrollAccountRole {
  id: string;
  name: string;
  code: string;
}

/**
 * The real `payroll.getDefaultPayrollAccounts` (#22374) response shape —
 * each role is nullable: the backend returns `null` for a role rather than
 * throwing when the entity's COA is missing or has mis-typed that fixed
 * code (see that procedure's own doc comment in `payroll.ts`).
 */
export interface GetDefaultPayrollAccountsResult {
  wages_expense: DefaultPayrollAccountRole | null;
  wages_payable: DefaultPayrollAccountRole | null;
}

/**
 * The four `payroll.*` calls {@link fetchPayRunRecords} needs, isolated
 * behind an injectable seam so the catch-block classification below (the
 * actual bug this seam exists to make testable — adversarial review, PR
 * #22370, finding 1) can be exercised with a fake client that throws a
 * real-shaped error, instead of only ever being exercised indirectly through
 * a rendered React hook. Mirrors `lib/auth/dev_employees.ts`'s
 * `DevEmployeesDeps` / `defaultDeps()` DI pattern.
 */
export interface PayRunsFetchDeps {
  listPayRuns: (
    input: PayRunsFetchInput,
  ) => Promise<{ pay_runs: PayRunSummaryRow[] }>;
  getPayRun: (
    input: PayRunsFetchInput & { pay_run_id: string },
  ) => Promise<{ pay_run: { lines: RealPayRunLine[] } }>;
  getPayRunJournalPreview: (
    input: PayRunsFetchInput & { pay_run_id: string },
  ) => Promise<RealPayRunJournalPreview>;
  /**
   * #22374 — resolves the two fixed-code wages accounts for an entity with
   * NO prior pay run to reuse ids from. Required (not optional): an optional
   * dep would let a test silently skip exercising the fallback path — see
   * {@link fetchPayRunRecords}'s doc comment for when this is called.
   */
  getDefaultPayrollAccounts: (
    input: PayRunsFetchInput,
  ) => Promise<GetDefaultPayrollAccountsResult>;
}

function defaultPayRunsFetchDeps(): PayRunsFetchDeps {
  const client = getTrpcClient();
  return {
    listPayRuns: (input) => client.payroll.listPayRuns.query(input),
    getPayRun: (input) => client.payroll.getPayRun.query(input),
    getPayRunJournalPreview: (input) =>
      client.payroll.getPayRunJournalPreview.query(input),
    getDefaultPayrollAccounts: (input) =>
      client.payroll.getDefaultPayrollAccounts.query(input),
  };
}

/** `createPayRun`'s two required GL account ids, resolved from pay-run history — see module docblock. */
export interface DefaultPayrollAccounts {
  wages_expense_account_id: string;
  wages_payable_account_id: string;
}

/**
 * Converts the real `payroll.getDefaultPayrollAccounts` (#22374) response
 * into the same both-or-null shape {@link resolveDefaultPayrollAccounts}
 * produces — `createPayRun` requires BOTH ids, so a partial pair (one role
 * resolved, the other `null`) is exactly as unusable as neither resolving.
 * Pure and exported so it's unit-testable without any network mocking,
 * mirroring {@link resolveDefaultPayrollAccounts}'s own pure-function-
 * extraction pattern.
 */
export function mapFallbackDefaultAccounts(
  result: GetDefaultPayrollAccountsResult,
): DefaultPayrollAccounts | null {
  if (result.wages_expense === null || result.wages_payable === null) {
    return null;
  }
  return {
    wages_expense_account_id: result.wages_expense.id,
    wages_payable_account_id: result.wages_payable.id,
  };
}

/**
 * Resolves the GL accounts a new pay run should post to, by reusing the
 * most-recently-created EXISTING pay run's own already-persisted
 * `wages_expense_account_id`/`wages_payable_account_id` — see module
 * docblock for why this (not `account.list`, not
 * `payroll.listPaymentSourceAccounts`) is the correct source. Pure and
 * synchronous so it's unit-testable without any network mocking.
 *
 * Returns `null` when no row carries BOTH ids (an entity with zero prior
 * pay runs, or — defensively — a legacy/corrupted row missing one of the
 * two) — never a guessed or partial result.
 *
 * Selection is by recency ONLY — `status` (including `voided`) is
 * deliberately NOT part of the filter. An earlier pass of this function
 * excluded `voided` rows on the theory that it protected against replaying
 * a stale/archived account after a chart-of-accounts reorg. Second-round
 * adversarial review (PR #22375, finding 1) established that reasoning was
 * backwards: void status is ORTHOGONAL to whether an account id is stale —
 * a `paid` run's account ids can be exactly as stale after a reorg, and
 * `paid` rows were never excluded by that logic, so it didn't actually
 * protect against the risk it claimed to. Worse, when the newest row
 * happened to be the one that got voided (e.g. a run created post-reorg
 * with the correct NEW accounts, voided for an unrelated reason like a
 * wrong amount), the old logic skipped it and fell back to a strictly OLDER
 * row — which is MORE likely to hold the stale/archived account, not less.
 * The real protection against a stale/archived account belongs in genuine
 * server-side validation on `createPayRun`/`approvePayRun` (tracked
 * separately as #22725 — `account.list`/`payroll.listPaymentSourceAccounts`
 * still can't supply these ids for the reuse-from-history path, see the
 * module docblock above) — this frontend selection should not pretend to
 * provide protection it doesn't.
 *
 * Ties on `created_at` (equal timestamps, or `listPayRuns` returning rows
 * in an unspecified D1 order) break on `id` (lexicographically largest) so
 * the choice is deterministic across re-fetches in the same session —
 * never "whichever row the database happened to return last."
 */
export function resolveDefaultPayrollAccounts(
  rows: readonly PayRunSummaryRow[],
): DefaultPayrollAccounts | null {
  let best: {
    id: string;
    created_at: string;
    wages_expense_account_id: string;
    wages_payable_account_id: string;
  } | null = null;

  for (const row of rows) {
    if (
      row.wages_expense_account_id === null ||
      row.wages_payable_account_id === null
    ) {
      continue;
    }
    const isNewer =
      best === null ||
      row.created_at > best.created_at ||
      (row.created_at === best.created_at && row.id > best.id);
    if (isNewer) {
      best = {
        id: row.id,
        created_at: row.created_at,
        wages_expense_account_id: row.wages_expense_account_id,
        wages_payable_account_id: row.wages_payable_account_id,
      };
    }
  }

  if (best === null) return null;
  return {
    wages_expense_account_id: best.wages_expense_account_id,
    wages_payable_account_id: best.wages_payable_account_id,
  };
}

export type PayRunsFetchResult =
  | {
      outcome: 'success';
      records: RealPayRunRecord[];
      /**
       * See {@link resolveDefaultPayrollAccounts} — `null` only when BOTH
       * the reuse-from-history resolution AND the `payroll.
       * getDefaultPayrollAccounts` fallback (#22374) failed to resolve a
       * usable pair. See {@link fetchPayRunRecords}'s doc comment for the
       * two-tier resolution order.
       */
      defaultAccounts: DefaultPayrollAccounts | null;
    }
  | { outcome: 'failure'; failure: PayRunsLoadFailure };

/**
 * Resolves the entity's real pay-run records, or classifies why it
 * couldn't. Exported (and DI'd via `deps`) so the FORBIDDEN
 * classification — the highest-risk piece of this whole hook, see module
 * docblock — is unit-testable against a fake client that throws a
 * real-shaped tRPC error, not just testable via the pure
 * {@link resolvePayRunsReadState} in isolation from the wiring that feeds
 * it. `usePayRuns` below is a thin caller: it owns React state and calls
 * this function with the real client (`deps` defaults to
 * {@link defaultPayRunsFetchDeps}).
 *
 * `defaultAccounts` resolution is two-tier (#22374, see module docblock's
 * "First-ever pay run" section): {@link resolveDefaultPayrollAccounts} over
 * the just-fetched `pay_runs` rows first (zero new calls, unconditionally
 * short-circuits when it resolves — an entity WITH history never reaches the
 * fallback), then, ONLY when that returns `null`, a single
 * `getDefaultPayrollAccounts` call. That fallback call is wrapped in its OWN
 * try/catch, deliberately separate from the outer one: a failure there (a
 * network error, an unexpected FORBIDDEN, a COA missing the fixed codes)
 * must never flip the overall outcome to `failure` — the pay-run history
 * fetched above is still perfectly good and must still render; only
 * `defaultAccounts` (and therefore `start_pay_run`) is affected.
 */
export async function fetchPayRunRecords(
  input: PayRunsFetchInput,
  deps: PayRunsFetchDeps = defaultPayRunsFetchDeps(),
): Promise<PayRunsFetchResult> {
  try {
    const { pay_runs } = await deps.listPayRuns(input);

    const resolved = await Promise.all(
      pay_runs.map(async (row): Promise<RealPayRunRecord> => {
        const { pay_run } = await deps.getPayRun({
          ...input,
          pay_run_id: row.id,
        });
        const journalPreview =
          row.status === 'draft'
            ? await deps.getPayRunJournalPreview({
                ...input,
                pay_run_id: row.id,
              })
            : null;

        return {
          id: row.id,
          period_start: row.period_start,
          period_end: row.period_end,
          status: row.status,
          total_gross_cents: row.total_gross_cents,
          created_at: row.created_at,
          approved_by: row.approved_by ?? null,
          approved_at: row.approved_at ?? null,
          lines: pay_run.lines,
          journal_preview: journalPreview,
        };
      }),
    );

    let defaultAccounts = resolveDefaultPayrollAccounts(pay_runs);
    if (defaultAccounts === null) {
      // Zero-history entity (or a legacy row missing one id) — try the
      // narrow backend fallback (#22374). Its own failure must never poison
      // the pay-run history this function already successfully resolved
      // above — see this function's doc comment.
      try {
        defaultAccounts = mapFallbackDefaultAccounts(
          await deps.getDefaultPayrollAccounts(input),
        );
      } catch (fallbackErr) {
        console.error(
          '[wagepro-web] pay runs: fallback default payroll account resolution failed -- start_pay_run stays unavailable, pay-run history unaffected:',
          fallbackErr,
        );
        defaultAccounts = null;
      }
    }

    return {
      outcome: 'success',
      records: resolved,
      defaultAccounts,
    };
  } catch (err) {
    if (isPayrollAddonRequiredError(err)) {
      // Deliberately worded differently from
      // `dev_pay_run_actions.ts`'s dev-bypass-only entitlement-guidance
      // string (that module's own build test asserts its EXACT phrasing
      // never leaks into a production bundle; this is real production
      // code, always bundled, and must not share that substring).
      console.error(
        '[wagepro-web] pay runs: real payroll data unavailable for this entity (payroll add-on not enabled) -- rendering the entitlement-required state, never the mock pay-run history.',
      );
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    console.error('[wagepro-web] Failed to resolve real pay-run history:', err);
    return { outcome: 'failure', failure: 'error' };
  }
}

export type PayRunsReadState =
  | { status: 'loading' }
  /** The OAuth grant has no bound entity — see `oauth_entity_context.ts`. Unreachable in practice: `main.tsx`'s `AuthGate` already gates the whole app on this via `useSessionFacts()` before `<WageProStoreProvider>` (and this hook) ever mounts. Handled defensively anyway, never a crash. */
  | { status: 'no_entity' }
  /** The entity is missing the `payroll` add-on entitlement (#21675). NEVER render `MOCK_PAY_RUNS` here — see module docblock. */
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; pay_runs: PayRun[]; current_run: PayRun | null };

/**
 * Pure derivation, exported separately from the hook below so it is
 * unit-testable without rendering React (mirrors `resolveSessionFactsState`/
 * `resolveMyPayState`'s "pure decision function + thin hook" split).
 *
 * Priority: `no_entity` always wins — then resolved `records` win over a
 * stale `failure` flag (a successful retry's records must display even if an
 * earlier attempt's failure was never explicitly cleared by the caller) —
 * then `failure` — then `loading`. In the real hook, `records`/`failure` are
 * reset together at the start of every fetch, so this ordering only matters
 * for a synthetic combination a test might construct directly (same caveat
 * `resolveSessionFactsState` documents).
 *
 * `current_run` is chosen by {@link select_current_run}.
 */
export function resolvePayRunsReadState(input: {
  entityContext: OAuthEntityContext;
  records: RealPayRunRecord[] | undefined;
  failure: PayRunsLoadFailure | undefined;
  /**
   * Employee records, for resolving each pay-run line's `employee_name`.
   *
   * Optional so existing callers/tests keep compiling, but passing it is what
   * makes line labels real: without it every line's name degrades to `'—'`
   * (see `wagepro_store_pay_runs.ts`'s documented fallback), which is what
   * made the post-run "Generate payslips" dropdown show five identical em
   * dashes.
   */
  employeeRecords?: readonly RealEmployeeRecord[] | undefined;
}): PayRunsReadState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.records !== undefined) {
    // `mock` is `[]`, never actually read: `derivePayRunsFromRecords` only
    // falls back to its `mock` argument when `records` is `undefined`, which
    // this branch has already excluded.
    const pay_runs = derivePayRunsFromRecords(
      [],
      input.records,
      input.employeeRecords,
    );
    return {
      status: 'ready',
      pay_runs,
      current_run: select_current_run(pay_runs),
    };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

// ── Write path (C7, #22329) ─────────────────────────────────────────────────

/**
 * The two real `payroll.*` write mutations {@link startPayRun}/
 * {@link approvePayRun} need, isolated behind an injectable seam — same
 * "test the wiring's FORBIDDEN classification directly" reasoning as
 * {@link PayRunsFetchDeps} above.
 */
export interface PayRunMutationDeps {
  createPayRun: (
    input: PayRunsFetchInput & {
      period_start: string;
      period_end: string;
      wages_expense_account_id: string;
      wages_payable_account_id: string;
    },
  ) => Promise<{
    pay_run_id: string;
    total_gross_cents: number;
    skipped_foreign_currency_employees: readonly SkippedForeignCurrencyEmployee[];
    /** V387: finalised pay runs that could not be re-priced for back pay. Absent on older servers. */
    back_pay_warnings?: readonly BackPayWarning[];
  }>;
  approvePayRun: (
    input: PayRunsFetchInput & { pay_run_id: string },
  ) => Promise<{ ok: boolean }>;
  /**
   * `payroll.finalisePayRun` (#23308) — sweeps approved payroll-method
   * expense claims into an APPROVED pay run's journal (Dr expense accounts,
   * Cr wages_payable_account_id). Requires `payroll:employer:write`. Never
   * mutates a run that isn't in `approved` state (server rejects with
   * BAD_REQUEST — the frontend hides the button in that case anyway).
   */
  finalisePayRun: (
    input: PayRunsFetchInput & { pay_run_id: string },
  ) => Promise<{ settled: number; total_cents: number }>;
  /**
   * `payroll.voidPayRun` (#23308) — reverses an APPROVED run's GL journal
   * and leave accruals, marks it `voided`. Server rejects `draft` and
   * already-voided runs (the frontend enforces the same state guard so the
   * button never opens the confirm dialog on an invalid row). Input schema
   * carries NO reason field: the WagePro history screen therefore never
   * prompts for one.
   */
  voidPayRun: (
    input: PayRunsFetchInput & { pay_run_id: string },
  ) => Promise<{ ok: boolean }>;
  /**
   * `payroll.deletePayRun` (#23308) — hard-deletes a `draft` pay run + its
   * lines so the period stops blocking `createPayRun`'s overlap guard. NEVER
   * touches a posted GL journal (there is none for a draft). Server rejects
   * any non-draft status; the frontend enforces the same state guard on
   * the button.
   */
  deletePayRun: (
    input: PayRunsFetchInput & { pay_run_id: string },
  ) => Promise<{ deleted: boolean }>;
  /**
   * `payroll.setPayRunLineAdjustment` (#23309) — one-time adjustment on a
   * DRAFT pay-run line. Passing `adjustment_cents: null` (or `0`) clears any
   * existing adjustment; the backend recovers the base pre-adjustment gross
   * so repeated edits are idempotent (see the mutation's own docblock).
   */
  setPayRunLineAdjustment: (
    input: PayRunsFetchInput & {
      pay_run_id: string;
      employee_id: string;
      adjustment_cents: number | null;
      adjustment_reason?: string | null;
    },
  ) => Promise<{
    pay_run_line_id: string;
    gross_pay_cents: number;
    deductions_cents: number;
    net_pay_cents: number;
    adjustment_cents: number | null;
    adjustment_reason: string | null;
  }>;
  /**
   * `payroll.setPayRunLineLumpSum` (#23309) — lump-sum portion on a DRAFT
   * pay-run line. `lump_sum_cents: null` (or `0`) clears. Backend enforces
   * `min(0)` on the input schema.
   */
  setPayRunLineLumpSum: (
    input: PayRunsFetchInput & {
      pay_run_id: string;
      employee_id: string;
      lump_sum_cents: number | null;
    },
  ) => Promise<{
    pay_run_line_id: string;
    gross_pay_cents: number;
    lump_sum_cents: number;
    deductions_cents: number;
    net_pay_cents: number;
  }>;
}

function defaultPayRunMutationDeps(): PayRunMutationDeps {
  const client = getTrpcClient();
  return {
    createPayRun: (input) => client.payroll.createPayRun.mutate(input),
    approvePayRun: (input) => client.payroll.approvePayRun.mutate(input),
    finalisePayRun: (input) => client.payroll.finalisePayRun.mutate(input),
    voidPayRun: (input) => client.payroll.voidPayRun.mutate(input),
    deletePayRun: (input) => client.payroll.deletePayRun.mutate(input),
    setPayRunLineAdjustment: (input) =>
      client.payroll.setPayRunLineAdjustment.mutate(input),
    setPayRunLineLumpSum: (input) =>
      client.payroll.setPayRunLineLumpSum.mutate(input),
  };
}

/**
 * How a real `payroll.*` write mutation ({@link startPayRun}/
 * {@link approvePayRun}) can fail — a superset of {@link PayRunsLoadFailure}
 * (reads aren't money-gated, so `'step_up_required'` is a write-path-only
 * addition, never surfaced by {@link fetchPayRunRecords}/
 * {@link resolvePayRunsReadState}). `'step_up_required'` classifies the
 * `assertMoneyWriteAllowed` (`apps/api/src/lib/assert_money_write_allowed.ts`)
 * AAL2 step-up rejection via {@link isStepUpRequired} — see that function's
 * own docblock (`lib/auth/step_up.ts`) for the exact wire shape it matches.
 */
/**
 * `'has_contractor'` (WagePro worker types): `approvePayRun` refused a draft
 * because one of its workers has since been recorded as a contractor
 * (`PAYROLL_PAY_RUN_HAS_CONTRACTOR`). Retrying can never succeed — the only
 * way forward is deleting the draft and creating it again (contractors are
 * left out automatically), so it gets its own guidance state, never the
 * generic `error` retry prompt.
 */
export type PayRunMutationFailure =
  | PayRunsLoadFailure
  | 'step_up_required'
  | 'has_contractor';

/**
 * An employee `payroll.createPayRun` left out of the run because their pay
 * currency isn't the entity's base currency (payroll runs in the base
 * currency only -- `apps/api/src/trpc/routers/payroll_pay_currency.ts`).
 */
export interface SkippedForeignCurrencyEmployee {
  id: string;
  full_name: string;
  pay_currency_code: string;
}

/**
 * A finalised pay run `payroll.createPayRun` could not re-price for back pay
 * (V387). `no_snapshot`: the run predates salary history, so its lines carry
 * no record of how gross was built. `currency_changed`: the pay currency for
 * that span moved, so a delta is meaningless. Either way the operator settles
 * it with a one-off adjustment instead of an automatic back-pay amount.
 */
export interface BackPayWarning {
  pay_run_id: string;
  reason: 'no_snapshot' | 'currency_changed';
}

export type PayRunMutationResult =
  | {
      outcome: 'success';
      /** Only {@link startPayRun} sets this; every other mutation omits it. */
      skipped_foreign_currency_employees?: readonly SkippedForeignCurrencyEmployee[];
      /** Only {@link startPayRun} sets this, and only when non-empty. */
      back_pay_warnings?: readonly BackPayWarning[];
    }
  | { outcome: 'failure'; failure: PayRunMutationFailure };

/**
 * Calls the real `payroll.createPayRun`. Never throws — every failure mode
 * (FORBIDDEN from either the `payroll` add-on gate or a missing OAuth scope,
 * any other rejection, INCLUDING `deps` resolution itself throwing
 * synchronously when the caller omits it — adversarial review finding 5)
 * resolves `{ outcome: 'failure', failure }` with one actionable
 * `console.error`, mirroring {@link fetchPayRunRecords}'s own never-throw
 * contract. `defaultAccounts` is the caller's responsibility to resolve
 * first via {@link resolveDefaultPayrollAccounts} — this function does not
 * re-derive it, so it stays a pure "make the one HTTP call" seam.
 */
export async function startPayRun(
  input: { period_start: string; period_end: string },
  entityInput: PayRunsFetchInput,
  defaultAccounts: DefaultPayrollAccounts,
  deps?: PayRunMutationDeps,
): Promise<PayRunMutationResult> {
  try {
    // Resolved INSIDE the try, not as a default parameter value -- a
    // default parameter is evaluated at call time but OUTSIDE this
    // function's own try/catch, so a synchronous throw from
    // `defaultPayRunMutationDeps()` (e.g. `getTrpcClient()`) would
    // otherwise reject this "never throws" function's promise with no
    // catch-block classification, and every caller in this codebase
    // chains a bare `.then()` with no `.catch()` (mirrors
    // `settings_screen.tsx`'s `handle_save` convention) -- an unhandled
    // rejection, not just a misreported error.
    const resolvedDeps = deps ?? defaultPayRunMutationDeps();
    const created = await resolvedDeps.createPayRun({
      ...entityInput,
      ...input,
      ...defaultAccounts,
    });
    const back_pay_warnings = created.back_pay_warnings ?? [];
    return {
      outcome: 'success',
      skipped_foreign_currency_employees:
        created.skipped_foreign_currency_employees,
      ...(back_pay_warnings.length > 0 ? { back_pay_warnings } : {}),
    };
  } catch (err) {
    // Defensive: `createPayRun` is not documented as `assertMoneyWriteAllowed`
    // -gated today, but checked anyway (never a silent fallthrough to the
    // generic `error` state) so a future gate change on this procedure
    // degrades to the correct classification automatically -- same
    // "defensive, not just reactive" stance `isPayrollAddonRequiredError`'s
    // own check below already takes.
    if (isStepUpRequired(err)) {
      console.error(
        '[wagepro-web] start pay run: AAL2 step-up required -- rendering the 2FA guidance state (run_payroll_screen.tsx renders pay_run.start_step_up_required).',
      );
      return { outcome: 'failure', failure: 'step_up_required' };
    }
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] start pay run: real payroll data unavailable for this entity (payroll add-on not enabled).',
      );
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    console.error('[wagepro-web] start pay run: createPayRun failed:', err);
    return { outcome: 'failure', failure: 'error' };
  }
}

/**
 * Calls the real, single-step `payroll.approvePayRun`. Never throws — same
 * contract as {@link startPayRun}. `approvePayRun` is gated by the
 * `wagepro_paid` product tier, NOT the `payroll` add-on entitlement (see
 * `apps/api/src/trpc/routers/payroll.ts`'s router-level comment), so
 * `isPayrollAddonRequiredError` realistically never matches here — checked
 * anyway (never a bare `isForbiddenTrpcError`) so a future gate change on
 * this procedure degrades to the correct classification automatically
 * rather than silently mis-reporting a product-tier failure as the
 * unrelated "payroll add-on required" prompt.
 *
 * `approvePayRun` is ALSO a real, genuine `assertMoneyWriteAllowed` money
 * -write gate (#22821) — signing off a pay run posts the statutory journal
 * entries, so a session that was never established with two-factor
 * authentication (AAL1) is rejected outright. That rejection resolves the
 * distinct `'step_up_required'` failure (see {@link isStepUpRequired}), not
 * the generic `'error'` state: blind retry can never succeed against an
 * AAL2 step-up requirement (an AAL1 token never self-upgrades), only a fresh
 * sign-in round trip does, so the caller (`wizard_steps.tsx`'s
 * `ApprovalStep`) needs to know which failure this is to point the user at
 * the right recovery, not a generic "please try again" that can never work.
 */
export async function approvePayRun(
  pay_run_id: string,
  entityInput: PayRunsFetchInput,
  deps?: PayRunMutationDeps,
): Promise<PayRunMutationResult> {
  try {
    // See `startPayRun`'s identical comment: resolved inside the try so a
    // synchronous throw from `defaultPayRunMutationDeps()` can't escape as
    // an unhandled rejection (adversarial review finding 5).
    const resolvedDeps = deps ?? defaultPayRunMutationDeps();
    await resolvedDeps.approvePayRun({ ...entityInput, pay_run_id });
    return { outcome: 'success' };
  } catch (err) {
    // `approvePayRun` IS `assertMoneyWriteAllowed`-gated (it posts the
    // statutory journal entries) -- an AAL1 session is rejected with
    // `UNAUTHORIZED` + `data.ledgerCode ===
    // 'LEDGER_AUTH_MONEY_WRITE_STEP_UP_REQUIRED'` (see `isStepUpRequired`'s
    // own docblock, `lib/auth/step_up.ts`). Blind retry can never succeed
    // here -- only a fresh sign-in round trip (`restartSignIn()`) upgrades an
    // AAL1 token -- so this MUST be classified and surfaced distinctly from
    // the generic `error` state, never collapsed into a "try again" prompt
    // the caller can't act on. Checked BEFORE `isPayrollAddonRequiredError`
    // since both are FORBIDDEN/UNAUTHORIZED-shaped tRPC errors matched by
    // duck-typed shape, not by error class.
    if (isStepUpRequired(err)) {
      console.error(
        '[wagepro-web] sign off pay run: AAL2 step-up required -- rendering the 2FA guidance state.',
      );
      return { outcome: 'failure', failure: 'step_up_required' };
    }
    if (isPayRunHasContractorError(err)) {
      console.error(
        '[wagepro-web] sign off pay run: the draft carries a worker now recorded as a contractor -- rendering the delete-and-recreate guidance state.',
      );
      return { outcome: 'failure', failure: 'has_contractor' };
    }
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] sign off pay run: real payroll data unavailable for this entity (payroll add-on not enabled).',
      );
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    console.error('[wagepro-web] sign off pay run: approvePayRun failed:', err);
    return { outcome: 'failure', failure: 'error' };
  }
}

/**
 * Calls the real, single-step `payroll.finalisePayRun` (#23308). Never
 * throws — same contract as {@link startPayRun}/{@link approvePayRun}.
 * `finalisePayRun` is admin-guarded server-side and posts the sweep
 * journal, but is NOT documented as `assertMoneyWriteAllowed`-gated today —
 * checked defensively so a future gate change degrades to the correct
 * classification automatically.
 */
export async function finalisePayRun(
  pay_run_id: string,
  entityInput: PayRunsFetchInput,
  deps?: PayRunMutationDeps,
): Promise<PayRunMutationResult> {
  try {
    const resolvedDeps = deps ?? defaultPayRunMutationDeps();
    await resolvedDeps.finalisePayRun({ ...entityInput, pay_run_id });
    return { outcome: 'success' };
  } catch (err) {
    if (isStepUpRequired(err)) {
      console.error('[wagepro-web] finalise pay run: AAL2 step-up required.');
      return { outcome: 'failure', failure: 'step_up_required' };
    }
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] finalise pay run: real payroll data unavailable for this entity (payroll add-on not enabled).',
      );
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    console.error(
      '[wagepro-web] finalise pay run: finalisePayRun failed:',
      err,
    );
    return { outcome: 'failure', failure: 'error' };
  }
}

/**
 * Calls the real `payroll.voidPayRun` (#23308). Never throws — same
 * contract as {@link finalisePayRun}.
 */
export async function voidPayRun(
  pay_run_id: string,
  entityInput: PayRunsFetchInput,
  deps?: PayRunMutationDeps,
): Promise<PayRunMutationResult> {
  try {
    const resolvedDeps = deps ?? defaultPayRunMutationDeps();
    await resolvedDeps.voidPayRun({ ...entityInput, pay_run_id });
    return { outcome: 'success' };
  } catch (err) {
    if (isStepUpRequired(err)) {
      console.error('[wagepro-web] void pay run: AAL2 step-up required.');
      return { outcome: 'failure', failure: 'step_up_required' };
    }
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] void pay run: real payroll data unavailable for this entity (payroll add-on not enabled).',
      );
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    console.error('[wagepro-web] void pay run: voidPayRun failed:', err);
    return { outcome: 'failure', failure: 'error' };
  }
}

/**
 * Calls the real `payroll.deletePayRun` (#23308). Never throws — same
 * contract as {@link finalisePayRun}. Only ever invoked for a `draft` run;
 * the server AND the frontend both enforce that state guard.
 */
export async function deletePayRun(
  pay_run_id: string,
  entityInput: PayRunsFetchInput,
  deps?: PayRunMutationDeps,
): Promise<PayRunMutationResult> {
  try {
    const resolvedDeps = deps ?? defaultPayRunMutationDeps();
    await resolvedDeps.deletePayRun({ ...entityInput, pay_run_id });
    return { outcome: 'success' };
  } catch (err) {
    if (isStepUpRequired(err)) {
      console.error('[wagepro-web] delete pay run: AAL2 step-up required.');
      return { outcome: 'failure', failure: 'step_up_required' };
    }
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] delete pay run: real payroll data unavailable for this entity (payroll add-on not enabled).',
      );
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    console.error('[wagepro-web] delete pay run: deletePayRun failed:', err);
    return { outcome: 'failure', failure: 'error' };
  }
}

/**
 * A per-line edit input for the wizard's Review Pay step (#23309). The
 * caller reads the new values off the modal's form state; either field may
 * be `undefined` when the user didn't change it, so the wrapper can decide
 * whether to fire zero, one, or two backend mutations for a single Save.
 * `adjustment_cents === null` / `lump_sum_cents === null` explicitly CLEAR
 * the respective delta (the pre-existing mutation contract; see
 * `setPayRunLineAdjustment`'s own docblock).
 */
export interface SetPayRunLineDeltasInput {
  pay_run_id: string;
  employee_id: string;
  adjustment_cents?: number | null;
  adjustment_reason?: string | null;
  lump_sum_cents?: number | null;
}

/**
 * Calls the real `payroll.setPayRunLineAdjustment` (#23309) — never throws,
 * mirrors {@link startPayRun}/{@link approvePayRun}'s contract. The backend
 * is `payrollWriteProcedure`-gated (add-on + admin-role), so a firm without
 * the payroll add-on resolves `'entitlement_required'` and every other
 * rejection collapses to `'error'` with one actionable `console.error`.
 * There is no `assertMoneyWriteAllowed` gate on this procedure today — the
 * money post happens later, at `approvePayRun` — so no `'step_up_required'`
 * classification is realistically reachable here. Checked defensively
 * anyway, so a future gate change on the procedure degrades to the correct
 * classification automatically instead of mis-reporting a 2FA rejection as
 * the generic `'error'` state (mirrors the same defensive check
 * `startPayRun` runs against its own not-currently-money-gated procedure).
 */
export async function setPayRunLineAdjustment(
  input: {
    pay_run_id: string;
    employee_id: string;
    adjustment_cents: number | null;
    adjustment_reason?: string | null;
  },
  entityInput: PayRunsFetchInput,
  deps?: PayRunMutationDeps,
): Promise<PayRunMutationResult> {
  try {
    const resolvedDeps = deps ?? defaultPayRunMutationDeps();
    await resolvedDeps.setPayRunLineAdjustment({ ...entityInput, ...input });
    return { outcome: 'success' };
  } catch (err) {
    if (isStepUpRequired(err)) {
      console.error(
        '[wagepro-web] set pay-run line adjustment: AAL2 step-up required.',
      );
      return { outcome: 'failure', failure: 'step_up_required' };
    }
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] set pay-run line adjustment: real payroll data unavailable for this entity (payroll add-on not enabled).',
      );
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    console.error(
      '[wagepro-web] set pay-run line adjustment: setPayRunLineAdjustment failed:',
      err,
    );
    return { outcome: 'failure', failure: 'error' };
  }
}

/**
 * Calls the real `payroll.setPayRunLineLumpSum` (#23309) — never throws;
 * same contract and rationale as {@link setPayRunLineAdjustment}.
 */
export async function setPayRunLineLumpSum(
  input: {
    pay_run_id: string;
    employee_id: string;
    lump_sum_cents: number | null;
  },
  entityInput: PayRunsFetchInput,
  deps?: PayRunMutationDeps,
): Promise<PayRunMutationResult> {
  try {
    const resolvedDeps = deps ?? defaultPayRunMutationDeps();
    await resolvedDeps.setPayRunLineLumpSum({ ...entityInput, ...input });
    return { outcome: 'success' };
  } catch (err) {
    if (isStepUpRequired(err)) {
      console.error(
        '[wagepro-web] set pay-run line lump sum: AAL2 step-up required.',
      );
      return { outcome: 'failure', failure: 'step_up_required' };
    }
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] set pay-run line lump sum: real payroll data unavailable for this entity (payroll add-on not enabled).',
      );
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    console.error(
      '[wagepro-web] set pay-run line lump sum: setPayRunLineLumpSum failed:',
      err,
    );
    return { outcome: 'failure', failure: 'error' };
  }
}

/**
 * Fires the caller-selected subset of `payroll.setPayRunLineAdjustment` /
 * `payroll.setPayRunLineLumpSum` for one pay-run line (#23309), in order.
 * Never throws; on the first failure, returns that failure and DOES NOT
 * attempt the second call — the backend is idempotent per-mutation but the
 * user's intent for the second field would be ambiguous once the first
 * partial write landed with an unexpected rejection.
 *
 * `adjustment_cents`/`lump_sum_cents` are `undefined` for "leave it alone",
 * `null`/`0` for "clear it", any other number for "set it to this".
 */
export async function setPayRunLineDeltas(
  input: SetPayRunLineDeltasInput,
  entityInput: PayRunsFetchInput,
  deps?: PayRunMutationDeps,
): Promise<PayRunMutationResult> {
  const resolvedDeps = deps ?? defaultPayRunMutationDeps();
  const { pay_run_id, employee_id } = input;

  if (input.adjustment_cents !== undefined) {
    const result = await setPayRunLineAdjustment(
      {
        pay_run_id,
        employee_id,
        adjustment_cents: input.adjustment_cents,
        adjustment_reason: input.adjustment_reason ?? null,
      },
      entityInput,
      resolvedDeps,
    );
    if (result.outcome === 'failure') return result;
  }

  if (input.lump_sum_cents !== undefined) {
    const result = await setPayRunLineLumpSum(
      {
        pay_run_id,
        employee_id,
        lump_sum_cents: input.lump_sum_cents,
      },
      entityInput,
      resolvedDeps,
    );
    if (result.outcome === 'failure') return result;
  }

  return { outcome: 'success' };
}

export interface UsePayRunsResult {
  state: PayRunsReadState;
  /** Re-fetches pay-run history from scratch. Meaningful for `error`/`entitlement_required` (e.g. the add-on was just granted) — mirrors `useMyPay`'s `retry`. */
  retry: () => void;
  acknowledge_exception: (run_id: string, exception_id: string) => void;
  /**
   * Signs off (approves) the given pay run against the real backend (C7,
   * #22329). `undefined` when the write path isn't reachable — either the
   * read state isn't `ready` (no real pay-run data to act on) or the OAuth
   * grant isn't write-capable (`!isWriteCapableGrant(grant)` — see module
   * docblock).
   * Resolves a {@link PayRunMutationResult}, never throws; a successful
   * approval triggers a real re-fetch (`retry()`'s own mechanism) so the
   * screen reflects the backend's confirmed state, never an optimistic
   * local flip.
   */
  sign_off: ((run_id: string) => Promise<PayRunMutationResult>) | undefined;
  /**
   * Starts a new pay run against the real backend (C7, #22329). `undefined`
   * when the write path isn't reachable (see `sign_off`'s doc comment for
   * the shared read-state/grant-mode gate) OR when NEITHER of
   * {@link resolveDefaultPayrollAccounts}'s reuse-from-history path NOR the
   * `payroll.getDefaultPayrollAccounts` fallback (#22374, see module
   * docblock's "First-ever pay run" section) could resolve `createPayRun`'s
   * required GL accounts — an entity whose COA is missing or has mis-typed
   * both the Wages Expense (`477`) and Wages Payable (`803`) accounts. Never
   * a guessed account id.
   */
  start_pay_run:
    | ((input: {
        period_start: string;
        period_end: string;
      }) => Promise<PayRunMutationResult>)
    | undefined;
  /**
   * Finalises the given pay run against the real backend (#23308) — the
   * wizard's terminal action, only offered on `approved` runs. `undefined`
   * when the write path isn't reachable (same shared read-state/grant-mode
   * gate as `sign_off`).
   */
  finalise_pay_run:
    | ((run_id: string) => Promise<PayRunMutationResult>)
    | undefined;
  /**
   * Voids the given pay run against the real backend (#23308). `undefined`
   * when the write path isn't reachable. The caller (history screen) is
   * responsible for only ever offering this on rows whose `status` is
   * `approved` — the server enforces the same guard, so a stale UI never
   * silently voids a non-approved run.
   */
  void_pay_run: ((run_id: string) => Promise<PayRunMutationResult>) | undefined;
  /**
   * Hard-deletes the given draft pay run against the real backend (#23308).
   * `undefined` when the write path isn't reachable. The caller (history
   * screen) is responsible for only ever offering this on rows whose
   * `status` is `draft` — the server enforces the same guard.
   */
  delete_pay_run:
    | ((run_id: string) => Promise<PayRunMutationResult>)
    | undefined;
  /**
   * Sets (or clears) the adjustment and/or lump-sum delta on a single
   * DRAFT-run pay-run line (#23309). `undefined` when the write path isn't
   * reachable — same shared read-state/grant-mode gate as `sign_off`/
   * `start_pay_run`. A successful save triggers a real re-fetch so the
   * screen reflects the backend's confirmed recomputed net pay rather than
   * an optimistic flip.
   */
  set_line_deltas:
    | ((input: SetPayRunLineDeltasInput) => Promise<PayRunMutationResult>)
    | undefined;
  /**
   * `true` when the OAuth grant is NOT write-capable — i.e.
   * `!isWriteCapableGrant(grant)`, covering both a `grant.mode === 'read'`
   * token AND a `mode: 'write'` grant whose `scopes` don't actually carry
   * `payroll:employer:write` (second-round adversarial review, PR #22375
   * finding 3) — exposed separately from `start_pay_run`/`sign_off`'s own
   * `undefined` check (first-round adversarial review finding 3) so a
   * caller can distinguish "you can't act because your access isn't
   * write-capable" from "you can't act because this entity has no prior
   * pay run to resolve default GL accounts from" (see module docblock) —
   * both collapse to `start_pay_run === undefined` on the empty state, but
   * only the first has a real explanation worth showing the user.
   */
  is_read_only_grant: boolean;
  /** Post-run actions for `PostRunStep` — see module docblock. */
  post_actions: PayRunPostActionsController | undefined;
  /**
   * `true` when calling `acknowledge_exception` for the CURRENT real pay
   * run would actually do something. (C12 follow-up, independent
   * adversarial review of PR #22453 finding F4, #22450.)
   *
   * `acknowledge_exception` (see this interface's own doc comment above)
   * writes into `useWageProStore()`'s `pay_runs` state, which is seeded
   * once from `MOCK_PAY_RUNS` and never updated from real data — so it
   * only actually mutates something when `state.current_run.id` happens to
   * match an id in that mock list, which for any real pay run it never
   * does. `false` in that case; `run_payroll_screen.tsx`/`ExceptionsStep`
   * use this to hide the "acknowledge" button entirely rather than let it
   * silently no-op. `true` whenever `state.status !== 'ready'` too (no real
   * run to check against yet) so the guard only ever HIDES the button, never
   * shows it somewhere it wasn't already going to render.
   */
  exception_ack_available: boolean;
}

/** Pay-run history plus the wizard actions for the run in progress. */
export function usePayRuns(): UsePayRunsResult {
  const entityContext = useOAuthEntityContext();
  // Needed to label pay-run lines with real employee names — see
  // `resolvePayRunsReadState`'s `employeeRecords` doc comment.
  const { records: employeeRecords } = useEmployeeRecords();
  const { grant } = useAuth();
  const {
    acknowledge_exception,
    pay_run_post_actions,
    pay_runs: mockPayRuns,
  } = useWageProStore();

  const [records, setRecords] = React.useState<RealPayRunRecord[] | undefined>(
    undefined,
  );
  const [defaultAccounts, setDefaultAccounts] = React.useState<
    DefaultPayrollAccounts | null | undefined
  >(undefined);
  const [failure, setFailure] = React.useState<PayRunsLoadFailure | undefined>(
    undefined,
  );
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    // Captured as its own consts so the nested async closure below
    // references definitely-string values rather than relying on narrowing
    // of `entityContext` surviving across the closure boundary — same
    // pattern `use_session_facts.ts`/`use_my_pay.ts` use.
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setRecords(undefined);
    setDefaultAccounts(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      // All catch-block classification (entitlement_required vs. error)
      // lives in `fetchPayRunRecords` — this hook only owns React state.
      // See that function's docblock for why the split exists.
      const result = await fetchPayRunRecords({
        firm_id: firmId,
        entity_id: entityId,
      });
      if (cancelled) return;
      if (result.outcome === 'success') {
        setRecords(result.records);
        setDefaultAccounts(result.defaultAccounts);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = resolvePayRunsReadState({
    entityContext,
    records,
    failure,
    employeeRecords,
  });

  // The single shared write-path gate: a real, successfully-read state AND
  // a write-capable OAuth grant. `entityContext` is re-narrowed into its own
  // const (rather than re-checked inline below) so the closures captured by
  // `start_pay_run`/`sign_off` reference definitely-`ready` values, same
  // "capture, don't re-narrow across a closure boundary" pattern the fetch
  // effect above already uses.
  const readyEntity =
    state.status === 'ready' && entityContext.status === 'ready'
      ? entityContext
      : null;
  // `readyEntity !== null` is folded into `canWrite` here; TS's aliased-
  // condition narrowing then treats `readyEntity` as definitely non-null
  // inside any branch already gated on `canWrite` below, so those branches
  // don't re-check it explicitly. `isWriteCapableGrant` (not a bare
  // `grant?.mode !== 'read'` check) is what actually enforces the
  // mode-AND-scopes invariant — see its own doc comment (adversarial
  // review, PR #22375 finding 3).
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const start_pay_run =
    canWrite && defaultAccounts
      ? async (input: {
          period_start: string;
          period_end: string;
        }): Promise<PayRunMutationResult> => {
          const result = await startPayRun(
            input,
            { firm_id: readyEntity.firm_id, entity_id: readyEntity.entity_id },
            defaultAccounts,
          );
          if (result.outcome === 'success') {
            setReloadToken((token) => token + 1);
          }
          return result;
        }
      : undefined;

  const sign_off = canWrite
    ? async (run_id: string): Promise<PayRunMutationResult> => {
        const result = await approvePayRun(run_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
        if (result.outcome === 'success') {
          setReloadToken((token) => token + 1);
        }
        return result;
      }
    : undefined;

  // #23308 — every terminal-state mutation triggers a re-fetch on success so
  // the UI reflects the backend's confirmed state (matches `sign_off`'s
  // "never optimistic" contract). Sharing the same `canWrite` gate is
  // deliberate — void/delete/finalise are all `payroll:employer:write`.
  const finalise_pay_run = canWrite
    ? async (run_id: string): Promise<PayRunMutationResult> => {
        const result = await finalisePayRun(run_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
        if (result.outcome === 'success') {
          setReloadToken((token) => token + 1);
        }
        return result;
      }
    : undefined;
  const void_pay_run = canWrite
    ? async (run_id: string): Promise<PayRunMutationResult> => {
        const result = await voidPayRun(run_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
        if (result.outcome === 'success') {
          setReloadToken((token) => token + 1);
        }
        return result;
      }
    : undefined;
  const delete_pay_run = canWrite
    ? async (run_id: string): Promise<PayRunMutationResult> => {
        const result = await deletePayRun(run_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
        if (result.outcome === 'success') {
          setReloadToken((token) => token + 1);
        }
        return result;
      }
    : undefined;
  const set_line_deltas = canWrite
    ? async (
        input: SetPayRunLineDeltasInput,
      ): Promise<PayRunMutationResult> => {
        const result = await setPayRunLineDeltas(input, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
        // Always refetch after an attempt — even a failure may leave partial
        // server state (adjustment succeeded, then lump-sum failed after in
        // {@link setPayRunLineDeltas}'s two-step sequence). Refetching on
        // failure too keeps the review-step table honest about what actually
        // persisted; leaving stale UI up would silently diverge from server
        // state until the user next reloaded.
        setReloadToken((token) => token + 1);
        return result;
      }
    : undefined;

  // See `exception_ack_available`'s doc comment on `UsePayRunsResult` --
  // `null` (no real current run to check, or not `ready` yet) is treated as
  // "available" since the caller never renders the acknowledge button in
  // that state anyway; this guard exists purely to hide it for a REAL
  // current run whose id has no counterpart in the store's mock list.
  const currentRealRun = state.status === 'ready' ? state.current_run : null;
  const exceptionAckAvailable =
    currentRealRun === null ||
    mockPayRuns.some((run) => run.id === currentRealRun.id);

  return {
    state,
    retry: () => {
      setReloadToken((token) => token + 1);
    },
    acknowledge_exception,
    sign_off,
    start_pay_run,
    finalise_pay_run,
    void_pay_run,
    delete_pay_run,
    set_line_deltas,
    is_read_only_grant: !isWriteCapableGrant(grant),
    post_actions: pay_run_post_actions,
    exception_ack_available: exceptionAckAvailable,
  };
}

export interface UsePayrollReportsResult {
  report_ids: readonly PayrollReportId[];
  is_loading: boolean;
}

/**
 * The statutory report catalogue for the Payroll Reports screen (#23307).
 * The catalogue is static: every id maps to exactly one server-side procedure
 * (`epf.getFormC`, `etf.getFormII`, `etf.getFormR4`, `apit.getMonthlyReturn`,
 * `apit.getAnnualReconciliation`) already registered in
 * `apps/api/src/trpc/oauth_allowlist.ts`. The list itself is frozen so callers
 * cannot mutate it; only the export action fires a network call, invoked from
 * the screen when the user clicks Export.
 */
const STATUTORY_REPORT_IDS: readonly PayrollReportId[] = Object.freeze([
  'epf_form_c',
  'etf_form_ii',
  'etf_form_r4',
  'apit_monthly',
  'apit_annual',
]);

export function usePayrollReports(): UsePayrollReportsResult {
  return { report_ids: STATUTORY_REPORT_IDS, is_loading: false };
}
